import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { hasFreshCvPreviewMark } from './utils/cvPreviewRestore';
import { diag } from './utils/diagLog';
import InlineError from './components/InlineError';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LowBandwidthProvider } from './contexts/LowBandwidthContext';
import { StudentHubProvider } from './contexts/StudentHubContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { PostingChooserProvider } from './contexts/PostingChooserContext';
import { NetworkProvider } from './contexts/NetworkContext';
import { NavGuardProvider } from './contexts/NavGuardContext';
import { Toaster } from 'sonner';
import AnimatedSplash from './components/AnimatedSplash';
import GuestSignInBanner from './components/GuestSignInBanner';
import './App.css';

// Switch splash variant: "luxury" | "energetic" | "minimal"
const SPLASH_VARIANT = "luxury";
import './components/brand.css';

// Pages
import Home from './pages/Home';
import FarmerHub from './pages/FarmerHub';
import LadiesHub from './pages/LadiesHub';
import CropHome from './pages/CropHome';
import MarketPrices from './pages/MarketPrices';
import Shop from './pages/Shop';
import AddAd from './pages/AddAd';
import Account from './pages/Account';
import MessagesInbox, { ChatPage } from './pages/Messages';
// More page removed - "My Account" tab now links directly to /account
import SettingsPage from './pages/Settings';
import NotificationPreferences from './pages/NotificationPreferences';
import SavedSearches from './pages/SavedSearches';
import Auth from './pages/Auth';
import Welcome from './pages/Welcome';
import Wallet from './pages/Wallet';
import WalletHistory from './pages/WalletHistory';
import ChooseAdToBoost from './pages/ChooseAdToBoost';
import AuthCallback from './pages/AuthCallback';
import AdDetails from './pages/AdDetails';
import SellerProfile from './pages/SellerProfile';
import StudentHub from './pages/StudentHubLandingV2';
import StudentBooksPage from './pages/StudentBooksPage';
import StudentCVPage from './pages/StudentCVPage';
import StudentListPage from './pages/StudentListPage';
import PublicCV from './pages/PublicCV';
import { FeatureGate } from './components/student-hub';
import BookDetails from './pages/BookDetails';
import CreateBook from './pages/CreateBook';
import OpportunityDetails from './pages/OpportunityDetails';
import CreateOpportunity from './pages/CreateOpportunity';
import CVBuilder from './pages/CVBuilder';
import StudentProfile from './pages/StudentProfile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import DeleteAccount from './pages/DeleteAccount';
import Contact from './pages/Contact';
import AdminModeration from './pages/AdminModeration';
import AdminChangePassword from './pages/AdminChangePassword';
import DevAdminBadge from './components/DevAdminBadge';
import AdminPendingAds from './pages/AdminPendingAds';
import AdminSafetyCenter from './pages/AdminSafetyCenter';
import HelpAnalyticsDashboard from './pages/HelpAnalyticsDashboard';
import AdminSystemHealth from './pages/AdminSystemHealth';
// Premium Admin Dashboard (Phase 1 + 2 + Stage B)
import AdminLayout from './pages/Admin/AdminLayout';
import AdminOverview from './pages/Admin/AdminOverview';
import AdminAds from './pages/Admin/AdminAds';
import AdminAuditLogs from './pages/Admin/AdminAuditLogs';
import AdminSettings from './pages/Admin/AdminSettings';
import MyBusiness from './pages/MyBusiness';
import AdminCleanupTools from './pages/Admin/AdminCleanupTools';
import AdminCleanupLegacyImages from './pages/Admin/AdminCleanupLegacyImages';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminBooks from './pages/Admin/AdminBooks';
import AdminSponsorships from './pages/Admin/AdminSponsorships';
import SponsorshipReport from './pages/Admin/SponsorshipReport';
// iter322 Phase C — Admin location-quality dashboard
import AdminLocationQuality from './pages/Admin/AdminLocationQuality';
import AdminReports from './pages/Admin/AdminReports';
import AdminPayments from './pages/Admin/AdminPayments';
import AdminSellerStories from './pages/Admin/AdminSellerStories';
import MyAds from './pages/MyAds';
import Favorites from './pages/Favorites';
import AccountBanned from './pages/AccountBanned';
import AccountSuspended from './pages/AccountSuspended';
import AccountViews from './pages/AccountViews';
import AccountResponses from './pages/AccountResponses';
import ReportAd from './pages/ReportAd';
import EditAd from './pages/EditAd';
import OnboardingRole from './pages/OnboardingRole';
import PublishSuccessScreen from './components/publish/PublishSuccessScreen';

// Components
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import PWAInstallBanner from './components/PWAInstallBanner';
import PWAUpdateBanner from './components/PWAUpdateBanner';
import CoinsPopup from './components/CoinsPopup';
import { COINS_DISABLED } from './config/coinsFlag';
import OnboardingTips from './components/OnboardingTips';
import RoleSelection from './components/RoleSelection';
import LowBandwidthIndicator from './components/LowBandwidthIndicator';
import OfflineBanner from './components/OfflineBanner';
import OfflineScreen from './components/OfflineScreen';
import ScrollToTop from './components/ScrollToTop';
import './components/RoleSelection.css';

// Role Selection Modal for new users (Google or Email)
// NOTE: Deprecated - replaced by OnboardingRole page at /onboarding
// Keeping as fallback for edge cases where v2 onboarding fails
function RoleSelectionModal() {
  const { user, showOnboarding, setShowOnboarding, updateUser } = useAuth();
  const [showRoleModal, setShowRoleModal] = useState(false);
  
  // Check if v2 onboarding is complete OR user has account_type set
  const isV2Complete = localStorage.getItem('onboarding_version_completed') === 'v2';
  const hasAccountType = !!(user?.account_type || user?.accountType || user?.profile_type);
  const onboardingCompleted = user?.onboarding_completed || user?.onboardingCompleted;
  
  useEffect(() => {
    // IMPORTANT: If v2 onboarding is complete OR user has account_type, NEVER show old role selection
    if (isV2Complete || hasAccountType || onboardingCompleted) {
      console.log('[RoleModal] V2 onboarding complete or account_type exists - hiding legacy modal');
      setShowRoleModal(false);
      setShowOnboarding(false);
      return;
    }
    
    // Show role selection only if:
    // 1. User is logged in
    // 2. showOnboarding flag is true
    // 3. User has no role set
    // 4. V2 onboarding is NOT complete AND no account_type
    console.log('[RoleModal] Checking conditions:', {
      hasUser: !!user,
      showOnboarding,
      role: user?.role,
      isV2Complete,
      hasAccountType,
      onboardingCompleted
    });
    
    // DISABLED: The new /onboarding page handles this now
    // Legacy modal should not show - redirect to /onboarding happens via ProtectedRoute
    setShowRoleModal(false);
  }, [user, showOnboarding, isV2Complete, setShowOnboarding, hasAccountType, onboardingCompleted]);
  
  const handleRoleComplete = (selectedRole) => {
    console.log('[RoleModal] Role selected and saved:', selectedRole);
    setShowRoleModal(false);
    setShowOnboarding(false);
    if (selectedRole) {
      updateUser({ role: selectedRole, needs_onboarding: false });
    }
  };
  
  // Always return null - legacy modal disabled in favor of /onboarding page
  return null;
  
  // Original render (kept for reference):
  // if (isV2Complete || hasAccountType) return null;
  // if (!showRoleModal) return null;
  // return (
  //   <div className="role-modal-overlay" data-testid="role-modal-overlay">
  //     <div className="role-modal-container">
  //       <RoleSelection onComplete={handleRoleComplete} isModal={true} />
  //     </div>
  //   </div>
  // );
}

/**
 * OnboardingGuard - Controls access to /onboarding route
 * - If user NOT logged in -> redirect to /welcome
 * - If user logged in AND onboarding completed -> redirect to home (can't revisit onboarding)
 * - If user logged in AND needs onboarding -> allow access
 */
function OnboardingGuard({ children }) {
  const { user, loading, token } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  const rawTokenOg = token || localStorage.getItem('token');
  const hasToken = rawTokenOg && rawTokenOg !== 'null' && rawTokenOg !== 'undefined';
  
  // Not logged in - go to welcome
  if (!user && !hasToken) {
    return <Navigate to="/welcome" state={{ from: location }} replace />;
  }
  
  // Loading user...
  if (!user && hasToken) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  // Check if onboarding is already complete
  const onboardingCompleted = user.onboarding_completed || user.onboardingCompleted;
  const accountType = user.account_type || user.accountType || user.profile_type;
  const needsOnboarding = !onboardingCompleted && !accountType;
  
  // If onboarding is complete, redirect away - user can't revisit /onboarding
  if (!needsOnboarding) {
    console.log('[OnboardingGuard] Onboarding already complete, redirecting to home');
    return <Navigate to="/" replace />;
  }
  
  // User needs onboarding - allow access
  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { loading, user, token, oauthInProgress, completeOAuthTransition } = useAuth();

  // Google OAuth commit signal: the "Signing you in…" overlay is dismissed
  // ONLY after the router has committed the authenticated destination ('/')
  // with the user present. This effect runs after Home's DOM commit (children
  // effects first, then this), so removing the overlay can only ever reveal
  // Home — never a Welcome/Login frame. No timers involved.
  useEffect(() => {
    if (oauthInProgress && user && location.pathname === '/') {
      completeOAuthTransition();
    }
  }, [oauthInProgress, user, location.pathname, completeOAuthTransition]);

  // Android renderer-death recovery: when the system killed the WebView while
  // the CV preview was open, the app cold-boots — jump straight back to the
  // CV builder (which reopens the preview from its restore mark). Native-only,
  // no-op on web.
  const cvRestoreDone = useRef(false);
  useEffect(() => {
    if (cvRestoreDone.current || loading || !user) return;
    cvRestoreDone.current = true;
    if (hasFreshCvPreviewMark() && !location.pathname.includes('/student-hub/cv-builder')) {
      diag('RESTORE_NAVIGATE_TO_CV');
      navigate('/student-hub/cv-builder');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  useEffect(() => {
    diag('ROUTE', { p: location.pathname });
  }, [location.pathname]);

  // Premium Admin shell routes — hide global header / bottom nav / popups
  const isPremiumAdminShell = /^#?\/admin\/(overview|ads|books|users|reports|system-health|audit-logs|cleanup|cleanup-legacy-images)\b/.test(
    (location.pathname || '') + (location.hash || '')
  );

  // Secondary/legal pages — clean Back navigation only: no bottom nav, no center + button
  const isSecondaryPage = /^\/(contact|privacy-policy|privacy|terms|delete-account)\/?$/.test(location.pathname || '');
  
  // Dynamic viewport height calculation for mobile
  // Fixes 100vh issue when mobile browser address bar shows/hides
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    // Set on mount
    setVH();
    
    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    
    // Also update after a short delay (handles soft keyboard)
    const delayedSet = () => setTimeout(setVH, 100);
    window.addEventListener('focusout', delayedSet);
    
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
      window.removeEventListener('focusout', delayedSet);
    };
  }, []);
  
  // Dismiss all toasts on route change
  useEffect(() => {
    const { toast } = require('sonner');
    toast.dismiss();
  }, [location.pathname]);
  
  // Handle OAuth callback synchronously - check both hash and search for session_id
  const hasSessionId = location.hash?.includes('session_id=') || 
                       location.search?.includes('session_id=') ||
                       window.location.search?.includes('session_id=');
  
  if (hasSessionId) {
    console.log('[AppRouter] Detected session_id, rendering AuthCallback');
    return <AuthCallback />;
  }
  
  // Show loading screen while auth is initializing
  if (loading) {
    return (
      <div className="app-container">
        <div className="loading-screen" data-testid="app-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="app-container">
      {/* Offline/Online banner */}
      <OfflineBanner />
      {/* Full-screen offline state — official W logo (iter353) */}
      <OfflineScreen />
      {/* Toast notifications - auto-dismiss after 4 seconds */}
      <Toaster 
        position="top-center"
        expand={false}
        richColors
        closeButton
        duration={4000}
        theme={isDarkMode ? 'dark' : 'light'}
        offset="120px"
        toastOptions={{
          style: {
            direction: document.dir || 'ltr',
            zIndex: 9999,
          },
          className: 'wassla-toast',
        }}
      />
      {/* Scroll to top on route change */}
      <ScrollToTop />
      {/* Global header for all pages — hidden on premium admin shell */}
      {!isPremiumAdminShell && <Header />}
      {/* Visible Dev-Admin pill — only renders if user.is_seed_admin */}
      <DevAdminBadge />
      <main className={`main-content${isPremiumAdminShell ? ' admin-shell-main' : ''}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/ladies" element={<LadiesHub />} />
          <Route path="/farm" element={<FarmerHub />} />
          <Route path="/farm/:cropSlug" element={<FarmerHub />} />
          <Route path="/crops" element={<FarmerHub />} />
          <Route path="/crop/:cropSlug" element={<FarmerHub />} />
          <Route path="/market-prices" element={<MarketPrices />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/add-ad" element={<ProtectedRoute><AddAd /></ProtectedRoute>} />
          <Route path="/create-ad" element={<ProtectedRoute><AddAd /></ProtectedRoute>} />
          <Route path="/publish/success" element={<ProtectedRoute><PublishSuccessScreen /></ProtectedRoute>} />
          <Route path="/booking" element={<Navigate to="/" replace />} />
          <Route path="/booking-hub" element={<Navigate to="/" replace />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/account/business" element={<ProtectedRoute><MyBusiness /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagesInbox /></ProtectedRoute>} />
          <Route path="/messages/:id" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/more" element={<Navigate to="/account" replace />} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
          <Route path="/account/alerts" element={<ProtectedRoute><SavedSearches /></ProtectedRoute>} />
          <Route path="/account/student-profile" element={<ProtectedRoute requireStudent><StudentProfile /></ProtectedRoute>} />
          <Route path="/account/views" element={<ProtectedRoute><AccountViews /></ProtectedRoute>} />
          <Route path="/account/responses" element={<ProtectedRoute><AccountResponses /></ProtectedRoute>} />
          <Route path="/wallet" element={COINS_DISABLED ? <Navigate to="/" replace /> : <ProtectedRoute><Wallet /></ProtectedRoute>} />
          <Route path="/wallet/history" element={COINS_DISABLED ? <Navigate to="/" replace /> : <ProtectedRoute><WalletHistory /></ProtectedRoute>} />
          <Route path="/wallet/boost/choose-ad" element={COINS_DISABLED ? <Navigate to="/" replace /> : <ProtectedRoute><ChooseAdToBoost /></ProtectedRoute>} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/onboarding" element={<OnboardingGuard><OnboardingRole /></OnboardingGuard>} />
          <Route path="/account-banned" element={<AccountBanned />} />
          <Route path="/account-suspended" element={<AccountSuspended />} />
          <Route path="/ad/:adId" element={<AdDetails />} />
          <Route path="/seller/:userId" element={<SellerProfile />} />
          <Route path="/ad/:adId/report" element={<ReportAd />} />
          <Route path="/my-ads" element={<ProtectedRoute><MyAds /></ProtectedRoute>} />
          <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/saved" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/edit-ad/:adId" element={<ProtectedRoute><EditAd /></ProtectedRoute>} />
          
          {/* Student Hub Routes */}
          <Route path="/student-hub" element={<StudentHub />} />
          
          {/* Canonical Student Hub Module Routes (feature-flagged) */}
          <Route path="/student-hub/books" element={<FeatureGate featureSlug="books"><StudentBooksPage /></FeatureGate>} />
          <Route path="/student-hub/books/search" element={<FeatureGate featureSlug="books"><StudentBooksPage /></FeatureGate>} />
          {/* Courses & Admissions routes — DISABLED (modules hidden, code preserved) */}
          {/* <Route path="/student-hub/courses" element={<FeatureGate featureSlug="courses"><StudentListPage category="courses-skills" /></FeatureGate>} /> */}
          {/* <Route path="/student-hub/courses/search" element={<FeatureGate featureSlug="courses"><StudentListPage category="courses-skills" /></FeatureGate>} /> */}
          <Route path="/student-hub/scholarships" element={<FeatureGate featureSlug="scholarships"><StudentListPage category="scholarships" /></FeatureGate>} />
          <Route path="/student-hub/scholarships/search" element={<FeatureGate featureSlug="scholarships"><StudentListPage category="scholarships" /></FeatureGate>} />
          <Route path="/student-hub/internships" element={<FeatureGate featureSlug="internships"><StudentListPage category="internships-jobs" /></FeatureGate>} />
          <Route path="/student-hub/internships/search" element={<FeatureGate featureSlug="internships"><StudentListPage category="internships-jobs" /></FeatureGate>} />
          {/* <Route path="/student-hub/admissions" element={<FeatureGate featureSlug="admissions"><StudentListPage category="admissions" /></FeatureGate>} /> */}
          {/* <Route path="/student-hub/admissions/search" element={<FeatureGate featureSlug="admissions"><StudentListPage category="admissions" /></FeatureGate>} /> */}
          <Route path="/student-hub/cv" element={<FeatureGate featureSlug="cv"><StudentCVPage /></FeatureGate>} />
          
          {/* Submit routes (protected) */}
          <Route path="/student-hub/books/new" element={<ProtectedRoute><FeatureGate featureSlug="books"><CreateBook /></FeatureGate></ProtectedRoute>} />
          <Route path="/student-hub/internships/new" element={<ProtectedRoute><FeatureGate featureSlug="internships"><CreateOpportunity /></FeatureGate></ProtectedRoute>} />
          <Route path="/student-hub/scholarships/new" element={<ProtectedRoute><FeatureGate featureSlug="scholarships"><CreateOpportunity /></FeatureGate></ProtectedRoute>} />
          
          {/* Legacy Student Category Routes (backward-compatible) */}
          <Route path="/student/books" element={<StudentBooksPage />} />
          <Route path="/student/internships" element={<FeatureGate featureSlug="internships"><StudentListPage category="internships-jobs" /></FeatureGate>} />
          <Route path="/student/scholarships" element={<FeatureGate featureSlug="scholarships"><StudentListPage category="scholarships" /></FeatureGate>} />
          <Route path="/student/admissions" element={<StudentListPage category="admissions" />} />
          <Route path="/student/courses" element={<StudentListPage category="courses-skills" />} />
          <Route path="/student/cv" element={<StudentCVPage />} />
          
          {/* Legacy alias routes */}
          <Route path="/student/books-notes" element={<StudentBooksPage />} />
          <Route path="/student/internships-jobs" element={<FeatureGate featureSlug="internships"><StudentListPage category="internships-jobs" /></FeatureGate>} />
          <Route path="/student/courses-skills" element={<StudentListPage category="courses-skills" />} />
          <Route path="/student/cv-tools" element={<StudentCVPage />} />
          <Route path="/student/opportunities" element={<FeatureGate featureSlug="internships"><StudentListPage category="internships-jobs" /></FeatureGate>} />
          
          {/* Student Hub Detail Routes */}
          <Route path="/student-hub/books/:bookId" element={<BookDetails />} />
          <Route path="/student-hub/cv-builder" element={<ProtectedRoute><CVBuilder /></ProtectedRoute>} />
          <Route path="/student-hub/opportunities/:oppId" element={<FeatureGate featureSlug="internships"><OpportunityDetails /></FeatureGate>} />
          <Route path="/student-hub/opportunities/new" element={<ProtectedRoute><FeatureGate featureSlug="internships"><CreateOpportunity /></FeatureGate></ProtectedRoute>} />
          <Route path="/cv/:username" element={<PublicCV />} />
          
          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
          <Route path="/admin/change-password" element={<ProtectedRoute requireAdmin><AdminChangePassword /></ProtectedRoute>} />
          <Route path="/admin/sponsorship-report/:sponsorshipId" element={<ProtectedRoute requireAdmin><SponsorshipReport /></ProtectedRoute>} />
          {/* Premium Admin Dashboard (Phase 1 + 2 + Stage B) */}
          <Route element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
            <Route path="/admin/overview" element={<AdminOverview />} />
            <Route path="/admin/ads" element={<AdminAds />} />
            <Route path="/admin/books" element={<AdminBooks />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/sponsorships" element={<AdminSponsorships />} />
            <Route path="/admin/seller-stories" element={<AdminSellerStories />} />
            <Route path="/admin/system-health" element={<AdminSystemHealth />} />
            <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/cleanup" element={<AdminCleanupTools />} />
            <Route path="/admin/cleanup-legacy-images" element={<AdminCleanupLegacyImages />} />
            {/* iter322 Phase C — Location-Quality Dashboard */}
            <Route path="/admin/location-quality" element={<AdminLocationQuality />} />
          </Route>
          <Route path="/admin/moderation" element={<ProtectedRoute requireAdmin><AdminModeration /></ProtectedRoute>} />
          <Route path="/admin/pending-ads" element={<ProtectedRoute requireAdmin><AdminPendingAds /></ProtectedRoute>} />
          <Route path="/admin/safety-center" element={<ProtectedRoute requireAdmin><AdminSafetyCenter /></ProtectedRoute>} />
          <Route path="/admin/help-analytics" element={<ProtectedRoute requireAdmin><HelpAnalyticsDashboard /></ProtectedRoute>} />
        </Routes>
      </main>
      {!isPremiumAdminShell && !isSecondaryPage && <BottomNav />}
      {/* iter291 — PWA install banner (renders only when the browser
          actually fires beforeinstallprompt; no fake download links). */}
      {!isPremiumAdminShell && <PWAInstallBanner />}
      {/* iter307 — PWA "New version available — Update now" banner.
          Sits above BottomNav, EN/AR, controlled SKIP_WAITING + reload. */}
      {!isPremiumAdminShell && <PWAUpdateBanner />}
      {!isPremiumAdminShell && <CoinsPopup />}
      {!isPremiumAdminShell && <OnboardingTips />}
      {/* FirstActionPrompt removed (2026-09) — one clean first-time onboarding
          flow only; the role picker is the single welcome experience. */}
      {!isPremiumAdminShell && <RoleSelectionModal />}
      <LowBandwidthIndicator />
    </div>
  );
}

function ProtectedRoute({ children, requireStudent = false, requireAdmin = false }) {
  const { user, loading, token, needsProfileType, retryAuth } = useAuth();
  const location = useLocation();
  
  // Show loading while auth state is being determined
  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  // Check for token in localStorage as a fallback (handles race conditions).
  // Guard against the literal strings "null" / "undefined" that older builds
  // could persist when JSON.stringify(null) leaked into localStorage; treating
  // those as truthy keeps the spinner stuck forever (see iter262 regression).
  const rawToken = token || localStorage.getItem('token');
  const hasToken = rawToken && rawToken !== 'null' && rawToken !== 'undefined';
  
  // If no user and no token, redirect to welcome
  if (!user && !hasToken) {
    console.log('[ProtectedRoute] No user and no token, redirecting to /welcome');
    return <Navigate to="/welcome" state={{ from: location }} replace />;
  }
  
  // If we have a token but no user yet, auth already finished resolving
  // (loading=false above) without producing a user.
  if (!user && hasToken) {
    // iter371 — if the context itself has no token, the localStorage value was
    // never validated (stale write / another tab) — send the user to sign in
    // instead of spinning forever. Non-destructive: a full page load will
    // adopt + validate the stored token if it's actually valid.
    if (!token) {
      console.log('[ProtectedRoute] Unvalidated localStorage token, redirecting to welcome');
      return <Navigate to="/welcome" state={{ from: location }} replace />;
    }
    // iter379 — /api/auth/me failed with a non-terminal outcome (5xx or a
    // network error/timeout). The old code rendered an unconditional
    // "Loading..." screen here that could never resolve — the infinite
    // spinner reported on Android. Guaranteed termination: show a visible
    // error with Retry instead.
    console.log('[ProtectedRoute] Auth resolved without a user, showing error + retry');
    return (
      <div className="loading-screen" data-testid="auth-error-screen">
        <InlineError onRetry={retryAuth} />
      </div>
    );
  }
  
  // Check if user needs onboarding (first login)
  const onboardingCompleted = user.onboarding_completed || user.onboardingCompleted;
  const accountType = user.account_type || user.accountType || user.profile_type;
  const needsOnboarding = !onboardingCompleted && !accountType;
  
  // Redirect to onboarding if needed (and not already on onboarding page)
  if (needsOnboarding && location.pathname !== '/onboarding') {
    console.log('[ProtectedRoute] User needs onboarding, redirecting to /onboarding');
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }
  
  // If route requires admin role and user is not admin
  if (requireAdmin && !user.is_admin) {
    console.log('[ProtectedRoute] Admin required but user is not admin, redirecting to /account');
    return <Navigate to="/account" state={{ from: location, error: 'not_authorized' }} replace />;
  }

  // Admin password-rotation gate (2026-05-10)
  // The seed admin (configured via SEED_ADMIN_* env vars in production) ships with `must_change_password=true`.
  // Block every admin-scope route until they post a new strong password.
  // `/admin/change-password` itself is whitelisted so the user can reach
  // the rotation form.
  if (
    requireAdmin &&
    user.is_admin &&
    user.must_change_password &&
    location.pathname !== '/admin/change-password'
  ) {
    console.log('[ProtectedRoute] Admin password rotation required, redirecting to /admin/change-password');
    return <Navigate to="/admin/change-password" state={{ from: location }} replace />;
  }
  
  // If route requires student role and user is not a student
  if (requireStudent && user.role !== 'student') {
    return <Navigate to="/account" state={{ from: location, roleRequired: 'student' }} replace />;
  }
  
  return children;
}

function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <Router>
      <AuthProvider>
        <LanguageProvider>
          <ThemeProvider>
            <LowBandwidthProvider>
              <StudentHubProvider>
                <FavoritesProvider>
                  <PostingChooserProvider>
                    <NetworkProvider>
                      <NavGuardProvider>
                        <AppRouter />
                        <GuestSignInBanner />
                      </NavGuardProvider>
                    </NetworkProvider>
                    {showSplash && <AnimatedSplash variant={SPLASH_VARIANT} onFinish={() => setShowSplash(false)} />}
                  </PostingChooserProvider>
                </FavoritesProvider>
              </StudentHubProvider>
            </LowBandwidthProvider>
          </ThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </Router>
  );
}


export default App;
